import '@yandex-int/messenger.widget/lib/ui/popup.css';

import React, {
    createContext,
    useCallback,
    useEffect,
    useRef,
    MouseEvent,
    useContext,
    ReactElement,
} from 'react';
import { Widget, popupUIFactory, YandexConfig, TargetPosition } from '@yandex-int/messenger.widget';
import noop from 'lodash/noop';
import { withInvariant } from '../../utils/withInvariant';
import { withRequiredProp } from '../../utils/withRequiredProp';
import { IconChat } from '../icon/IconChat';
import { Button } from '../input/Button';
import { IconButton } from '../input/IconButton';

interface MessengerWidgetPayload {
    mount: (target: HTMLElement, targetPosition?: TargetPosition) => VoidFunction;
    open: (target: HTMLElement) => void;
    pasteMessage: (pasteText: string) => void;
}

const MessengerWidgetContext = createContext<MessengerWidgetPayload | null>(null);

const config = new YandexConfig({ serviceId: -1 });

const ui = popupUIFactory({});

const widget = new Widget(config).setUI(ui);

if (typeof window !== 'undefined') {
    widget.init();
}

interface MessengerWidgetProviderProps {
    guid?: string;
    children: ReactElement;
}

export const MessengerWidgetProvider = withRequiredProp<MessengerWidgetProviderProps, 'guid'>(
    (props) => {
        const { guid, children } = props;
        const open = useCallback(() => widget.show({ guid }), [guid]);
        const pasteMessage = useCallback(
            (pasteText: string) => widget.api.pasteMessage({ pasteText, guid }),
            [guid],
        );
        const mount = useCallback((target: HTMLElement, targetPosition?: TargetPosition) => {
            ui.setPopupTarget(target, targetPosition);

            return () => widget.hide();
        }, []);

        useEffect(() => {
            ui.mount();
        }, []);

        return (
            <MessengerWidgetContext.Provider value={{ open, mount, pasteMessage }}>
                {children}
            </MessengerWidgetContext.Provider>
        );
    },
    (props) => props.children,
    'guid',
);

interface MessengerButtonProps {
    onClick: () => void;
    message?: string;
    buttonText?: string;
    targetPosition?: TargetPosition;
}

export const MessengerButton = withInvariant(
    (props: MessengerButtonProps) => {
        const { message, onClick, buttonText, targetPosition } = props;
        const buttonRef = useRef<HTMLButtonElement | null>(null);
        const widgetContext = useContext(MessengerWidgetContext);
        const onWidgetOpen = useCallback(
            (e: MouseEvent<HTMLButtonElement>) => {
                widgetContext?.open(e.currentTarget);
                if (message) {
                    widgetContext?.pasteMessage(message);
                }
                onClick();
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [widgetContext, onClick, message],
        );

        useEffect(() => {
            if (!buttonRef.current || !widgetContext) {
                return noop;
            }

            return widgetContext.mount(buttonRef.current, targetPosition);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [widgetContext, buttonRef.current, targetPosition]);

        if (!widgetContext) {
            return null;
        }

        if (buttonText) {
            return (
                <Button
                    ref={buttonRef}
                    onClick={onWidgetOpen}
                    variant="filled"
                    width="100%"
                    color="secondary"
                >
                    {buttonText}
                </Button>
            );
        }

        return (
            <IconButton ref={buttonRef} variant="fab" size="xl" onClick={onWidgetOpen}>
                <IconChat />
            </IconButton>
        );
    },
    () => null,
);
